import { Box, Skeleton, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import { CameraTimesItemDto } from "~/api/GeneratedApi";
import GenericTable, {
  CustomColumn,
  CustomTableProps,
} from "~/components/GenericTable";
import { dayInSeconds, VideoClip } from "./VideoClip";

// function timeout(ms: number) {
//   return new Promise((resolve) => {
//     setTimeout(resolve, ms);
//   });
// }

export type CustomTime = {
  id: number;
  endExclusive: Date;
  startInclusive: Date;
};

export const defaultTime = dayjs("2000-01-01");
export const VideoTimesList = ({
  availableTimes,
  cameraId,
  selectedDate,
  availableTimesLoading,
}: {
  availableTimes: CameraTimesItemDto[] | undefined;
  cameraId: number;
  selectedDate: Dayjs;
  availableTimesLoading: boolean;
}) => {
  const { t } = useTranslation("");
  const [selectedTime, setSelectedTime] = React.useState<
    CustomTime | undefined
  >();

  const selectedDateTitle = React.useMemo(() => {
    if (!selectedDate) return "";
    return selectedDate.format("DD/MM/YYYY");
  }, [selectedDate]);

  const customDate = React.useMemo(
    () =>
      (availableTimes ?? []).reduce((items, time) => {
        return items.concat([
          {
            id: time.startInclusive.valueOf(),
            endExclusive: time.endExclusive,
            startInclusive: time.startInclusive,
          },
        ]);
      }, [] as CustomTime[]),
    [availableTimes],
  );

  const columns = React.useMemo((): CustomColumn<CustomTime>[] => {
    return [
      {
        field: "endExclusive",
        title: t("recordedStream:timeStamp"), // pvm titleen?
        width: "min-content",
        render: ({ endExclusive, startInclusive }) => {
          const timeInMs = dayjs(endExclusive).diff(dayjs(startInclusive));
          return (
            <Box sx={{ display: "flex", gap: "10px" }}>
              <Typography>
                {`${dayjs(startInclusive).format("HH:mm")} - 
                ${
                  timeInMs / (dayInSeconds * 1000) === 1 ||
                  dayjs(startInclusive).day() !== dayjs(endExclusive).day()
                    ? "24:00"
                    : dayjs(endExclusive).format("HH:mm")
                }`}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "duration" as CustomColumn<CustomTime>["field"],
        title: t("recordedStream:duration"),
        width: "min-content",
        render: ({ endExclusive, startInclusive }) => {
          // if (!endExclusive || !startInclusive) return null;
          const timeInMs = dayjs(endExclusive).diff(dayjs(startInclusive));

          const timeDifference = defaultTime.add(timeInMs, "millisecond");

          //   console.log(dayjs(timeInMs).toDate());
          if (timeInMs / (dayInSeconds * 1000) === 1)
            return (
              <Box sx={{ display: "flex", gap: "10px" }}>
                <Typography>24:00h</Typography>
              </Box>
            );
          return (
            <Box sx={{ display: "flex", gap: "10px" }}>
              <Typography>{timeDifference.format("HH:mm")}h</Typography>
            </Box>
          );
        },
        customSort: (
          { endExclusive, startInclusive },
          { endExclusive: endExclusive1, startInclusive: startInclusive1 },
        ) => {
          const timeInMs = dayjs(endExclusive).diff(dayjs(startInclusive));
          const timeInMs2 = dayjs(endExclusive1).diff(dayjs(startInclusive1));

          if (timeInMs < timeInMs2) {
            return -1;
          }
          if (timeInMs > timeInMs2) {
            return 1;
          }

          return 0;
        },
      },
    ];
  }, [t]);

  const tableProps = React.useMemo(
    (): CustomTableProps<CustomTime> => ({
      title: t("recordedStream:recordedTimes", {
        pvm: selectedDateTitle,
      }),
      options: {
        filtering: false,
        pageSize: 5,
        idSynonym: "id",
        search: false,
        paging: false,
        rowStyle: (rowData) => {
          const row = rowData as CustomTime;

          const style =
            row?.id === selectedTime?.id
              ? { backgroundColor: "#4fc3f74d" }
              : {};
          return style;
        },
      },
      onRowClick: (_e, availableTime) => setSelectedTime(availableTime),

      actions: [
        // {
        //   icon: "delete",
        //   tooltip: t("general:delete"),
        // },
        // {
        //   icon: "add",
        //   tooltip: t("general:add"),
        //   onClick: () => setShowStepper(true),
        //   isFreeAction: true,
        // },
      ],
    }),
    [selectedDateTitle, selectedTime?.id, t],
  );

  if (availableTimesLoading) return <Skeleton />;
  return (
    <Box
      sx={{
        display: "grid",
        gap: "35px",
        "& div": {
          boxShadow: "none",
        },
      }}
    >
      <GenericTable
        columns={columns}
        tableProps={tableProps}
        data={customDate ?? []}
      />
      {/* maybe key here? */}
      <VideoClip
        key={selectedTime?.id}
        cameraId={cameraId}
        selectedTime={selectedTime}
      />
    </Box>
  );
};
